/**
 * Product launch timer helpers.
 * Countdown is computed on the client from launch_datetime — no polling.
 */

import { useEffect, useState } from 'react'

const JUST_LAUNCHED_MS = 24 * 60 * 60 * 1000

/** Parse API datetime. Naive values from API are treated as Indian timezone (Asia/Kolkata). */
export const parseLaunchDate = (value) => {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const raw = String(value).trim()
  if (!raw) return null
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)
  const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
  // If no timezone info, treat as Indian timezone (Asia/Kolkata = UTC+5:30)
  const date = new Date(hasTimezone ? normalized : `${normalized.slice(0, 19)}+05:30`)
  return Number.isNaN(date.getTime()) ? null : date
}

export const getSecondsRemaining = (product, nowMs = Date.now()) => {
  if (!product?.launch_enabled) return 0
  const launchAt = parseLaunchDate(product.launch_datetime)
  if (!launchAt) return 0
  return Math.max(0, Math.floor((launchAt.getTime() - nowMs) / 1000))
}

export const isLaunchLocked = (product, nowMs = Date.now()) =>
  Boolean(product?.launch_enabled && getSecondsRemaining(product, nowMs) > 0)

export const isLaunchCompleted = (product, nowMs = Date.now()) =>
  !product?.launch_enabled || getSecondsRemaining(product, nowMs) <= 0

export const isJustLaunched = (product, nowMs = Date.now()) => {
  if (!product?.launch_enabled || !product?.launch_datetime) return false
  if (isLaunchLocked(product, nowMs)) return false
  const launchAt = parseLaunchDate(product.launch_datetime)
  if (!launchAt) return false
  const elapsed = nowMs - launchAt.getTime()
  return elapsed >= 0 && elapsed < JUST_LAUNCHED_MS
}

export const getDisplayLaunchBadge = (product, nowMs = Date.now()) => {
  if (isLaunchLocked(product, nowMs)) {
    return product.launch_badge || 'Coming Soon'
  }
  if (isJustLaunched(product, nowMs)) {
    return 'Just Launched'
  }
  return null
}

export const formatCountdownParts = (totalSeconds) => {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0))
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  return { days, hours, minutes, seconds: secs, total: seconds }
}

export const formatCountdownLabel = (totalSeconds) => {
  const { days, hours, minutes, seconds } = formatCountdownParts(totalSeconds)
  if (days > 0) {
    return `${days}d ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export const PRODUCT_TYPE_LABELS = {
  accessory: 'Accessory',
  gear: 'Gear',
  spare: 'Spare',
  combo: 'Combo',
}

export const getProductTypeLabel = (productType) =>
  PRODUCT_TYPE_LABELS[productType] || (productType ? String(productType) : 'Product')

/**
 * Live countdown tick (1s). Returns derived launch UI state for a product.
 */
export const useProductLaunchState = (product) => {
  const [nowMs, setNowMs] = useState(() => Date.now())

  const needsTicker = Boolean(
    product?.launch_enabled && product?.launch_datetime
  )

  useEffect(() => {
    if (!needsTicker) return undefined
    setNowMs(Date.now())
    const id = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(id)
  }, [needsTicker, product?.id, product?.launch_datetime, product?.launch_enabled])

  const secondsRemaining = getSecondsRemaining(product, nowMs)
  const locked = isLaunchLocked(product, nowMs)
  const justLaunched = Boolean(
    product?.launch_enabled &&
    !locked &&
    isJustLaunched(product, nowMs)
  )
  const badge = locked
    ? (product?.launch_badge || 'Coming Soon')
    : (justLaunched ? 'Just Launched' : null)
  const label = product?.launch_label || 'Launching In'
  const bannerColor = product?.launch_banner_color || '#ffcc32'

  return {
    secondsRemaining,
    locked,
    justLaunched,
    badge,
    label,
    bannerColor,
    completed: !locked,
    formatted: formatCountdownLabel(secondsRemaining),
    parts: formatCountdownParts(secondsRemaining),
  }
}
